import { Component, Input, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: "app-single-field",
  templateUrl: "./single-field.component.html",
  styleUrls: ["./single-field.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleFieldComponent),
      multi: true,
    },
  ],
})
export class SingleFieldComponent implements ControlValueAccessor {
  @Input() label!: string;

  @Input() displayValue: any;

  @Input() editMode = false;

  @Input() options?: { value: any; label: string; translate: boolean }[] = [{ value: 0, label: "", translate: false }];

  @Input() type? = "text";
  @Input() isInput = false;

  @Input() prefix?: string;
  @Input() suffix?: string;

  @Input() maxlength?: number;
  @Input() min?: number;

  @Input() required = false;
  @Input() requiredError = false;

  value: any = null;

  /**
   * Propagates a new value to the form model, replaced by the form's callback in registerOnChange().
   * @param value The new value
   */
  onChange: (value: any) => void = () => {
    return;
  };

  /**
   * Marks the field as touched in the form model, replaced by the form's callback in registerOnTouched().
   */
  onTouched: () => void = () => {
    return;
  };

  /**
   * Writes a new value from the form model into the component.
   * @param value New value to display
   */
  writeValue(value: any): void {
    this.value = value;
  }

  /**
   * Registers the callback to invoke when the value changes.
   * @param onChange Callback to invoke with the new value
   */
  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  /**
   * Registers the callback to invoke when the field is touched.
   * @param onTouched Callback to invoke on touch
   */
  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  /**
   * Handles input changes, converting the raw value to a number when needed and propagating it.
   * @param event Input event triggered by the field
   */
  onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;

    const newValue = raw === "" ? null : this.type === "number" ? Number(raw) : raw;

    this.value = newValue;
    this.onChange(newValue);
  }

  /**
   * Handles selection changes on the dropdown and propagates the selected option's value.
   * @param event Change event triggered by the select
   */
  onSelect(event: Event): void {
    const select = event.target as HTMLSelectElement;

    const newValue = this.options?.[select.selectedIndex]?.value;

    this.value = newValue;
    this.onChange(newValue);
  }
}
